import { Suspense, lazy } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom'

import { AuthProvider } from '@/auth/AuthProvider'
import { ProtectedRoute } from '@/auth/ProtectedRoute'
import { ImpersonatePage } from '@/pages/auth/ImpersonatePage'
import { LoginPage } from '@/pages/auth/LoginPage'
import { OAuthCallbackPage } from '@/pages/auth/OAuthCallbackPage'
import { PasswordResetConfirmPage } from '@/pages/auth/PasswordResetConfirmPage'
import { PasswordResetRequestPage } from '@/pages/auth/PasswordResetRequestPage'
import { RegisterPage } from '@/pages/auth/RegisterPage'
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage'
import { AppShell } from '@/layouts/AppShell'
import { CockpitLayout } from '@/pages/cockpit/CockpitLayout'
import { DocumentsTab } from '@/pages/cockpit/DocumentsTab'
import { RecordsTab } from '@/pages/cockpit/RecordsTab'
import { SettingsTab } from '@/pages/cockpit/SettingsTab'
import TemplateTab from '@/pages/cockpit/TemplateTab'
import { FragenPage } from '@/pages/fragen/FragenPage'
import { OverviewPage } from '@/pages/overview/OverviewPage'
import { ProjektePage } from '@/pages/projekte/ProjektePage'
import { SettingsPage } from '@/pages/einstellungen/SettingsPage'
import { BulkReviewPage } from '@/pages/review/BulkReviewPage'
import { AnalysePage } from '@/pages/onboarding/AnalysePage'
import { PickFormPage } from '@/pages/onboarding/PickFormPage'
import { ResultPage } from '@/pages/onboarding/ResultPage'
import { ConfirmTemplatePage } from '@/pages/templates/ConfirmTemplatePage'
import { FxSpinner } from '@/components/fx'
import { ImpersonationToast } from '@/layouts/ImpersonationToast'

/** The editor is the largest screen in the app and most sessions never open
 *  it, so its canvas stays out of the cockpit's first chunk. */
const EditorPage = lazy(() => import('@/pages/editor/EditorPage'))
const ArchivePage = lazy(() => import('@/pages/archiv/ArchivePage'))
const ReviewPage = lazy(() => import('@/pages/review/ReviewPage'))

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Server state is cached and invalidated rather than re-fetched on
      // every mount; the Flutter version's refresh logic was spread across
      // controllers and this is the one place it lives now.
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // A 401 is handled by the API client's refresh-and-retry; retrying
        // it here would fight that. Nor is a 403 or a 404 worth repeating.
        const status = (error as { status?: number })?.status
        if (status === 401 || status === 403 || status === 404) return false
        return failureCount < 2
      },
    },
  },
})

/**
 * A redirect that keeps the template id. `<Navigate>` takes a literal path,
 * so a renamed route under `/templates/:templateId` needs the parameter read
 * back out of the match before it can be put into the new one.
 */
function RedirectUnderTemplate({ to }: { to: (templateId: string) => string }) {
  const { templateId = '' } = useParams()
  return <Navigate to={to(templateId)} replace />
}

/** The fallback a lazily loaded screen shows while its chunk arrives. */
function Loading({ label }: { label: string }) {
  return (
    <main className="grid min-h-screen place-items-center">
      <FxSpinner label={label} />
    </main>
  )
}

/**
 * The route table, kept apart from the providers around it so it can be
 * mounted in a `MemoryRouter` and asserted on. Which paths are public, and
 * where a renamed one now points, is otherwise only ever found by clicking.
 */
export function CockpitRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/passwort-vergessen" element={<PasswordResetRequestPage />} />
      <Route path="/passwort-neu" element={<PasswordResetConfirmPage />} />
      {/* The two addresses the gateway actually puts in outgoing mail
          (`internal/auth/service.go:404` and `:522`). They are routes of
          their own rather than redirects onto the German paths above,
          because both pages consume the `?token=` that arrives with them
          and a redirect that dropped it would turn every reset and
          confirmation mail into a dead link. */}
      <Route path="/auth/password-reset/confirm" element={<PasswordResetConfirmPage />} />
      <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
      {/* Where the gateway's OAuth callback lands, with a one-time code. */}
      <Route path="/auth/callback" element={<OAuthCallbackPage />} />
      {/* Where a staff member's "Als Nutzer ansehen" opens: a one-time
          impersonation code in the URL fragment. Outside `ProtectedRoute` —
          nobody is signed in yet when this loads — and outside `AppShell`,
          same as the other bare auth screens. */}
      <Route path="/auth/impersonate" element={<ImpersonatePage />} />

      {/* Picking a form does not need an account — that is the point of the
          funnel — so this one route sits outside the gate.

          The funnel's wizard chrome is rendered by each page rather than by
          a layout route: `AnalysePage` advances the header as its own upload
          and detection proceed, so which step is current is a property of the
          page's state and not of the path. */}
      <Route path="/onboarding" element={<PickFormPage />} />

      <Route element={<ProtectedRoute />}>
        {/* Uploads on mount, so it has to stay behind the gate. The funnel
            keeps its own chrome and therefore sits outside the app shell. */}
        <Route path="/onboarding/analyse" element={<AnalysePage />} />
        <Route path="/onboarding/ergebnis/:templateId/:documentId" element={<ResultPage />} />

        {/* Everything the signed-in app proper is made of hangs off one
            shell: the rail on desktop, a top bar and drawer below 992px, the
            bottom nav at phone width. One layout route rather than a shell
            per screen, because two shells drift. */}
        <Route element={<AppShell />}>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/projekte" element={<ProjektePage />} />
          <Route
            path="/archiv"
            element={
              <Suspense fallback={<Loading label="Archiv wird geladen …" />}>
                <ArchivePage />
              </Suspense>
            }
          />
          {/* The tab is a path segment, so a settings tab is linkable and
              survives a refresh; `SettingsPage` redirects an unknown one. */}
          <Route path="/einstellungen" element={<SettingsPage />} />
          <Route path="/einstellungen/:tab" element={<SettingsPage />} />
          <Route path="/fragen" element={<FragenPage />} />

        </Route>

        {/* Everything inside a template is **outside** the shell: the design
            swaps the 80px rail for the template's own full-width header, whose
            "← Übersicht" is the way back. Nesting these in the shell gave them
            the rail the artboards do not draw and pushed every one of them
            68px to the right of where it belongs. */}
        <Route path="/templates/:templateId" element={<CockpitLayout />}>
          <Route index element={<DocumentsTab />} />
          <Route path="datensaetze" element={<RecordsTab />} />
          <Route path="vorlage" element={<TemplateTab />} />
          <Route path="einstellungen" element={<SettingsTab />} />
        </Route>

        {/* Under the same prefix but outside the cockpit layout too: each is a
            screen of its own, not a tab, and `cockpitTabFromPath`
            deliberately selects none of the four for them. */}
        <Route path="/templates/:templateId/felder-bestaetigen" element={<ConfirmTemplatePage />} />
        <Route path="/templates/:templateId/unklarheiten" element={<BulkReviewPage />} />
        <Route
          path="/templates/:templateId/dokumente/:documentId"
          element={
            <Suspense fallback={<Loading label="Dokument wird geladen …" />}>
              <ReviewPage />
            </Suspense>
          }
        />

        {/* The editor keeps the whole viewport — it has no rail and no tab
            strip — so it sits outside the shell. The path carries the **page
            number**, not an index into `pageList`: page numbers are never
            compacted, so a deleted layout leaves a permanent gap. */}
        <Route
          path="/templates/:templateId/bearbeiten/:pageNumber"
          element={
            <Suspense fallback={<Loading label="Editor wird geladen …" />}>
              <EditorPage />
            </Suspense>
          }
        />

        {/* "Prüfen" is the review screen's name in the design, so confirming
            the detected fields moved off that path before the two collided.
            The old address stays a redirect so an open tab does not 404. */}
        <Route
          path="/templates/:templateId/pruefen"
          element={<RedirectUnderTemplate to={(id) => `/templates/${id}/felder-bestaetigen`} />}
        />
        {/* `FirstScanPage` is gone: uploading the first scan is what the
            funnel's analyse and result steps do now. */}
        <Route
          path="/templates/:templateId/erster-scan"
          element={<RedirectUnderTemplate to={(id) => `/templates/${id}`} />}
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export function CockpitApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <CockpitRoutes />
          <ImpersonationToast />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
